import { ClipboardCheck } from "lucide-react";
import { Link, useSearchParams } from "react-router";

import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { diasDeEspera, formatearDecimal } from "@/components/informes/formato";
import { describirEntrega, formatearMomento } from "@/lib/fechas";
import { Button } from "@/components/ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/hooks/useAuth";
import { useRevisionesPendientes, useRevisoresDeInformes } from "@/hooks/useRevisiones";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import { urlDeLaEdicion } from "@/lib/origenDeNavegacion";
import { esSuperAdmin } from "@/lib/permisos";
import type { RevisionPendiente } from "@/lib/revisiones";

const FILAS_SKELETON = 5;

/**
 * Revisiones pendientes (pantalla `21 RV`, RF-G1): la bandeja del revisor.
 *
 * Lista los informes cuya cotización se envió a visar y todavía nadie visó,
 * del que lleva MÁS tiempo esperando al que menos: es una cola de espera, y lo
 * que se atiende primero es lo que hace más rato que espera. Ordena, filtra y
 * pagina el backend; el front solo traduce los controles a query params.
 *
 * QUÉ VE CADA UNO (RF-G1: "veo únicamente [lo mío] (el perfil Super Admin ve
 * todas)"): quien la acota es la API —`RevisionPendienteController` filtra por
 * el usuario de la sesión—, no esta pantalla. Acá el perfil decide solo lo que
 * se DIBUJA: el filtro por revisor es del Super Admin, porque al resto la
 * bandeja ya le llega acotada y un select para elegir a otra persona ofrecería
 * una cola que el servidor no le va a entregar.
 *
 * Falla CERRADO, igual que `lib/permisos.ts`: mientras la sesión no resuelve
 * no hay filtro. Aparece cuando se confirma que es Super Admin, no antes.
 *
 * El filtro vive en la URL y no en `useState` para que recargar, compartir el
 * enlace o volver con el botón "atrás" conserven lo que el usuario eligió.
 */
export function RevisionesPendientes() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const puedeFiltrarPorRevisor = esSuperAdmin(user?.perfil);

  // El `revisor` de la URL se IGNORA para quien no es Super Admin. La API ya
  // le acota la bandeja a lo suyo, así que un enlace compartido con el id de
  // otra persona se intersectaría en vacío y la pantalla diría "no hay nada"
  // sobre una cola que sí tiene filas.
  const revisorId = puedeFiltrarPorRevisor
    ? Number(params.get("revisor")) || undefined
    : undefined;
  const pagina = Number(params.get("pagina")) || 1;

  const bandeja = useRevisionesPendientes({ revisorId, pagina });
  const revisores = useRevisoresDeInformes({ habilitado: puedeFiltrarPorRevisor });
  const estadoConsulta = estadoDeConsulta(bandeja);

  /**
   * Cambiar el filtro vuelve SIEMPRE a la página 1: quedarse en la 3 después
   * de filtrar deja la tabla vacía sobre un resultado que sí tiene filas, y se
   * lee como "este revisor no tiene nada pendiente".
   */
  function cambiarFiltro(clave: string, valor: string) {
    const siguiente = new URLSearchParams(params);
    if (valor) siguiente.set(clave, valor);
    else siguiente.delete(clave);
    siguiente.delete("pagina");
    setParams(siguiente);
  }

  function urlDePagina(n: number): string {
    const siguiente = new URLSearchParams(params);
    if (n > 1) siguiente.set("pagina", String(n));
    else siguiente.delete("pagina");
    return `?${siguiente.toString()}`;
  }

  function irAPagina(evento: React.MouseEvent, n: number) {
    // Los controles son enlaces REALES (con href) para que el teclado y el
    // clic con rueda funcionen; el `preventDefault` es lo que evita la recarga
    // completa y deja la navegación del lado del cliente.
    evento.preventDefault();
    const siguiente = new URLSearchParams(params);
    if (n > 1) siguiente.set("pagina", String(n));
    else siguiente.delete("pagina");
    setParams(siguiente);
  }

  const total = bandeja.data?.total ?? 0;
  const porPagina = bandeja.data?.porPagina ?? 25;
  const desde = total === 0 ? 0 : (pagina - 1) * porPagina + 1;
  const hasta = Math.min(pagina * porPagina, total);
  const totalPaginas = bandeja.data?.totalPaginas ?? 1;

  return (
    <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <div>
        <p className="text-muted-foreground text-caption">
          {bandeja.data
            ? `${bandeja.data.totalGeneral} ${
                bandeja.data.totalGeneral === 1 ? "revisión pendiente" : "revisiones pendientes"
              }`
            : "Cotizaciones enviadas a revisión, de la que lleva más tiempo esperando a la más reciente."}
        </p>
        <p className="text-ink-hint mt-0.5 max-w-[70ch] text-xs">
          {puedeFiltrarPorRevisor
            ? "Ves las revisiones de todo el equipo. Filtra por revisor para ver la cola de una persona."
            : "Ves solo las cotizaciones en las que eres revisor."}
        </p>
      </div>

      {/* Solo el Super Admin elige de quién es la cola. Al resto la API ya le
          entrega la suya (RF-G1), así que el select no tendría qué ofrecer: la
          fila entera desaparece en vez de quedar como un control muerto. */}
      {puedeFiltrarPorRevisor && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label
              htmlFor="filtro-revisor"
              className="text-ink-muted text-xs font-bold whitespace-nowrap"
            >
              Revisor
            </label>
            <div className="w-[13.75rem]">
              <NativeSelect
                id="filtro-revisor"
                size="sm"
                value={params.get("revisor") ?? ""}
                onChange={(evento) => cambiarFiltro("revisor", evento.target.value)}
              >
                <option value="">Todos</option>
                {/* Solo quienes OCUPAN un rol de revisor, no todos los usuarios
                    asignables: ofrecer a alguien que nunca revisó nada es
                    prometer una respuesta que la bandeja no tiene. */}
                {revisores.data?.map((revisor) => (
                  <option key={revisor.id} value={revisor.id}>
                    {revisor.nombre}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>
        </div>
      )}

      {estadoConsulta.hayError ? (
        <EstadoDeError
          titulo="No pudimos cargar las revisiones"
          error={bandeja.error}
          onReintentar={() => bandeja.refetch()}
          reintentando={estadoConsulta.recargando}
        />
      ) : estadoConsulta.sinConexion ? (
        <EstadoSinConexion que="las revisiones" />
      ) : !estadoConsulta.cargando && total === 0 ? (
        <Empty className="rounded-xl border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ClipboardCheck />
            </EmptyMedia>
            <EmptyTitle>No hay revisiones pendientes</EmptyTitle>
            <EmptyDescription>
              {revisorId
                ? "Este revisor no tiene cotizaciones esperando su visado."
                : puedeFiltrarPorRevisor
                  ? "Cuando se envíe una cotización a revisión va a aparecer aquí."
                  : "Cuando te asignen una cotización para revisar va a aparecer aquí."}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <>
          <div className="bg-card overflow-x-auto rounded-xl border">
            <Table className="min-w-[65rem]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="px-6">Cliente</TableHead>
                  <TableHead>Revisor de la cotización</TableHead>
                  <TableHead>Revisor del informe final</TableHead>
                  <TableHead className="text-right">Precio</TableHead>
                  {/* "Entrega" a secas y no "Entrega comprometida": lo que la
                      celda muestra es el PLAZO pactado, que corre a contar de
                      la visita de inspección. Un día del calendario aparece
                      solo en las cotizaciones aceptadas bajo el modelo
                      anterior. */}
                  <TableHead>Entrega</TableHead>
                  <TableHead>En espera</TableHead>
                  <TableHead className="px-6 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {estadoConsulta.cargando
                  ? Array.from({ length: FILAS_SKELETON }).map((_, i) => (
                      <TableRow
                        key={i}
                        aria-label="Cargando revisión"
                        className="hover:bg-transparent"
                      >
                        <TableCell className="px-6">
                          <Skeleton className="h-3.5 w-56" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-3.5 w-32" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-3.5 w-32" />
                        </TableCell>
                        <TableCell className="text-right">
                          <Skeleton className="ml-auto h-3.5 w-16" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-3.5 w-24" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-3.5 w-24" />
                        </TableCell>
                        <TableCell className="px-6 text-right">
                          <Skeleton className="ml-auto h-8 w-52 rounded-md" />
                        </TableCell>
                      </TableRow>
                    ))
                  : bandeja.data?.revisiones.map((revision) => (
                      <FilaDeRevision key={revision.id} revision={revision} />
                    ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-ink-hint text-xs">
              {total === 0 ? "Mostrando 0" : `Mostrando ${desde}–${hasta} de ${total}`}
            </p>

            {totalPaginas > 1 && (
              <Pagination className="mx-0 w-auto justify-end">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href={urlDePagina(pagina - 1)}
                      aria-label="Página anterior"
                      aria-disabled={pagina === 1}
                      className={pagina === 1 ? "pointer-events-none opacity-50" : undefined}
                      onClick={(evento) => irAPagina(evento, pagina - 1)}
                    />
                  </PaginationItem>

                  {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((n) => (
                    <PaginationItem key={n}>
                      <PaginationLink
                        href={urlDePagina(n)}
                        isActive={n === pagina}
                        onClick={(evento) => irAPagina(evento, n)}
                      >
                        {n}
                      </PaginationLink>
                    </PaginationItem>
                  ))}

                  <PaginationItem>
                    <PaginationNext
                      href={urlDePagina(pagina + 1)}
                      aria-label="Página siguiente"
                      aria-disabled={pagina === totalPaginas}
                      className={
                        pagina === totalPaginas ? "pointer-events-none opacity-50" : undefined
                      }
                      onClick={(evento) => irAPagina(evento, pagina + 1)}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function FilaDeRevision({ revision }: { revision: RevisionPendiente }) {
  const cotizacion = revision.cotizacion;
  const precio = cotizacion ? formatearDecimal(cotizacion.precio_uf) : null;

  return (
    <TableRow>
      <TableCell className="px-6">
        <div className="flex flex-col">
          <span className="text-ink text-[0.8125rem] font-bold">{revision.cliente_nombre || "—"}</span>
          {/* La propiedad, debajo y en menor jerarquía: el revisor reconoce el
              trabajo por el predio, no solo por quién lo encargó. */}
          <span className="text-ink-hint text-xs">{revision.propiedad_nombre || "Sin predio"}</span>
        </div>
      </TableCell>

      {/* `null` acá es "sin revisor asignado", no un dato que no se consultó:
          el endpoint siempre manda las dos claves. */}
      <TableCell className="text-muted-foreground font-semibold">
        {revision.revisor_cotizacion?.nombre ?? "—"}
      </TableCell>

      <TableCell className="text-muted-foreground font-semibold">
        {revision.revisor_informe?.nombre ?? "—"}
      </TableCell>

      <TableCell className="text-muted-foreground text-right font-bold">
        {precio ? <span className="font-mono">{precio} UF</span> : "—"}
      </TableCell>

      <TableCell className="text-muted-foreground font-semibold">
        {cotizacion ? (
          <span className="font-mono">{describirEntrega(cotizacion.fecha_entrega, cotizacion.plazo_dias_habiles)}</span>
        ) : (
          "—"
        )}
      </TableCell>

      <TableCell className="text-muted-foreground font-semibold">
        {/* La espera es el criterio de orden de la bandeja, así que también se
            muestra: con la fecha sola, el revisor tiene que hacer la resta en
            la cabeza para saber cuál está más atrasada. */}
        {cotizacion?.generada_en ? <Espera desde={cotizacion.generada_en} /> : "—"}
      </TableCell>

      <TableCell className="px-6">
        <div className="flex items-center justify-end gap-2">
          {/* RF-G1: "CUANDO la selecciono, accedo al detalle de revisión".
              Va al VISADO y no a `/informes/:id/cotizacion/documento`: aquella
              es la vista previa del papel que recibe el cliente, sin las
              acciones del revisor y con la salida apuntando al flujo del
              ejecutivo. Se parecen; no son la misma pantalla. */}
          <Button asChild size="sm">
            <Link to={`/revisiones/${revision.id}`}>Revisar cotización</Link>
          </Button>

          {/* RF-G2: corregir el informe también se ofrece desde la fila, para
              el revisor que ya sabe qué está mal y no necesita abrir el
              visado. */}
          <Button asChild variant="outline" size="sm">
            <Link to={urlDeLaEdicion(revision.id, "revisiones")}>Ver / Editar</Link>
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function Espera({ desde }: { desde: string }) {
  const dias = diasDeEspera(desde);

  return (
    <span title={`Enviada a revisión el ${formatearMomento(desde)}`}>
      {dias === 0 ? "Hoy" : dias === 1 ? "Hace 1 día" : `Hace ${dias} días`}
    </span>
  );
}
